
import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import TutorialCreator from "@/components/tutorial-creator/TutorialCreator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tutorial } from "@/types/tutorial";

const TutorialCreatorPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get('edit');

  // Fetch existing tutorial if editing
  const { data: existingTutorial, isLoading } = useQuery({
    queryKey: ['tutorial-edit', editId],
    queryFn: async () => {
      if (!editId) return null;
      
      const { data, error } = await supabase
        .from('tutorials')
        .select('*')
        .eq('id', editId)
        .single();
      
      if (error) throw error;
      
      // Transform database tutorial to Tutorial interface
      const transformedTutorial: Tutorial = {
        id: data.id,
        title: data.title,
        coverImage: data.cover_image || "",
        shortDescription: data.short_description || "",
        tags: data.tags || [],
        bom: (data.content as any)?.bom || [],
        sections: (data.content as any)?.sections || [],
        status: (data.status as "draft" | "pending" | "approved" | "rejected") || "draft",
        authorId: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
      
      return transformedTutorial;
    },
    enabled: !!editId
  });

  const handleBack = () => {
    navigate('/tutorials');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header pageName="Tutorial Creator" useBackButton={true} />
        <div className="container max-w-4xl py-6 pt-[64px]">
          <Card>
            <CardHeader>
              <CardTitle>Loading...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header pageName={editId ? "Edit Tutorial" : "Create Tutorial"} useBackButton={true} />
      <div className="flex-1 pt-[64px]">
        <TutorialCreator
          onBack={handleBack}
          existingTutorial={existingTutorial || undefined}
        />
      </div>
    </div>
  );
};

export default TutorialCreatorPage;
